import type { IAggFunc, IAggFuncParams } from 'ag-grid-community';

/**
 * Factory that returns an aggFunc aggregating text values into a comma-separated string of unique values.
 *
 * Suitable for columns where each leaf row has a string value and group rows
 * should display a deduplicated summary (e.g. "BTC-USD, ETH-USD").
 *
 * An optional `getLabel` callback can be provided to transform each value before aggregation,
 * useful when the raw value differs from the desired display label (e.g. strategy code → name).
 *
 * In multi-level grouping scenarios, aggFunc runs bottom-up: child group rows are aggregated
 * first, so by the time a parent group's aggFunc runs, `values` may already contain
 * comma-separated strings from child groups. This function splits those back into individual
 * items before deduplicating, so values stay correctly unique at every level.
 */
export function createCsvTextAggFunc<TData = unknown>(
  getLabel?: (v: string) => string
): IAggFunc<TData, string | undefined> {
  return ({ values }: IAggFuncParams<TData, string | undefined>) => {
    const unique = [
      ...new Set(
        values
          .filter((v): v is string => v != null)
          .flatMap(v => v.split(', '))
          .map(v => (getLabel ? getLabel(v) : v))
      ),
    ];
    return unique.length > 0 ? unique.join(', ') : undefined;
  };
}

/** Pre-built csvTextAggFunc with no label transformation. Use {@link createCsvTextAggFunc} when a getLabel transform is needed. */
export const csvTextAggFunc = createCsvTextAggFunc();
